import {
  deviceStatus, serverStatus, byUrgency, countStatuses, health, DISK_CRITICAL,
} from "./dashboardHelpers.js";
import { Band, CardGrid, Section, KpiTile, LiveIndicator, Chip } from "./DashboardParts.jsx";
import { NasCard, GatewayCard, ServerCard } from "./DeviceCards.jsx";

// ══════════════════════════════════════════════════════
//  Onglet Supervision — UDM, puis NAS (avec leurs dernières sauvegardes),
//  puis serveurs. Les appareils en problème remontent en premier dans
//  chaque bande ; tout se rafraîchit seul (voir useSupervisionData).
// ══════════════════════════════════════════════════════

export default function SupervisionTab({ data, onNavigate }) {
  const { nas, gateways, servers, loading, refreshing, lastRefresh, errors, reload, intervalMs } = data;

  const sortedGateways = [...gateways].sort(byUrgency(deviceStatus, g => g.name));
  const sortedNas      = [...nas].sort(byUrgency(deviceStatus, n => n.name));
  const sortedServers  = [...servers].sort(byUrgency(serverStatus, s => s.hostname));

  const all = [
    ...gateways.map(deviceStatus),
    ...nas.map(deviceStatus),
    ...servers.map(serverStatus),
  ];
  const c = countStatuses(all);
  const serversOffline  = servers.filter(s => s.status !== "ONLINE").length;
  const serversDiskCrit = servers.filter(s => s.diskTotalGb && (s.diskUsedGb / s.diskTotalGb) * 100 >= DISK_CRITICAL).length;

  return (
    <div>
      {/* ── Indicateur temps réel (l'horloge est dans la barre d'onglets) ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <LiveIndicator lastRefresh={lastRefresh} refreshing={refreshing} intervalMs={intervalMs} onRefresh={reload} />
      </div>

      {/* ── Chiffres clés ── */}
      <div className="dash-metrics-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12, marginBottom: 20 }}>
        <KpiTile label="Appareils supervisés" value={all.length} />
        <KpiTile label="En bonne santé" value={c.OK} color={health("OK").color} />
        <KpiTile label="Avertissements" value={c.WARNING} color={c.WARNING ? health("WARNING").color : "var(--text-3)"} />
        <KpiTile label="Critiques" value={c.CRITICAL} color={c.CRITICAL ? health("CRITICAL").color : "var(--text-3)"} />
      </div>

      {/* ── 1. UDM / passerelles UniFi ── */}
      <Band title="UDM / Passerelles UniFi" statuses={loading ? null : gateways.map(deviceStatus)}>
        <Section loading={loading} error={errors.monitoring} count={sortedGateways.length}
          empty="Aucune passerelle enregistrée — ajoute-en depuis la page Supervision.">
          <CardGrid>{sortedGateways.map(g => <GatewayCard key={g.id} gateway={g} />)}</CardGrid>
        </Section>
      </Band>

      {/* ── 2. NAS Synology + dernières sauvegardes ── */}
      <Band title="NAS Synology" statuses={loading ? null : nas.map(deviceStatus)}>
        <Section loading={loading} error={errors.monitoring} count={sortedNas.length}
          empty="Aucun NAS enregistré — ajoute-en depuis la page Supervision.">
          <CardGrid>{sortedNas.map(n => <NasCard key={n.id} nas={n} />)}</CardGrid>
        </Section>
      </Band>

      {/* ── 3. Serveurs (agents Atera) ── */}
      <Band title="Serveurs" extra={<><Chip>Source : agents Atera</Chip>
        <span onClick={() => onNavigate?.("serveurs")} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "var(--accent-dim)", color: "var(--accent)", border: "1px solid rgba(230,51,41,.2)", cursor: "pointer" }}>Voir le détail →</span></>}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          {[
            ["Serveurs", servers.length, "var(--text-1)"],
            ["En ligne", servers.length - serversOffline, "var(--success)"],
            ["Hors ligne", serversOffline, serversOffline ? "var(--danger)" : "var(--text-3)"],
            ["Disque critique", serversDiskCrit, serversDiskCrit ? "var(--danger)" : "var(--text-3)"],
          ].map(([label, value, color]) => (
            <div key={label} style={{ flex: "1 1 130px", background: "var(--card-bg)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px" }}>
              <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "Syne,sans-serif", color }}>{value}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>{label}</div>
            </div>
          ))}
        </div>
        <Section loading={loading} error={errors.servers} count={sortedServers.length}
          empty="Aucun serveur supervisé pour le moment.">
          <CardGrid maxHeight={640}>{sortedServers.map(s => <ServerCard key={s.id} server={s} />)}</CardGrid>
        </Section>
      </Band>
    </div>
  );
}